// 使用AWS SDK处理不带客户端密钥的应用客户端

import { 
  CognitoIdentityProviderClient, 
  InitiateAuthCommand, 
  SignUpCommand, 
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  RespondToAuthChallengeCommand,
  GetUserCommand,
  UpdateUserAttributesCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand
} from '@aws-sdk/client-cognito-identity-provider';
import { createSrpSession, signSrpSession } from 'cognito-srp-helper';

// AWS配置 - 从 generated.ts 加载（从 Infisical 和 .env.general 获取）
import { APP_CONFIG } from '../config/generated';
import { logger } from '../utils/logger';

let REGION: string = APP_CONFIG.AWS_REGION || 'ap-northeast-1';
let USER_POOL_ID: string = APP_CONFIG.USER_POOL_ID || '';
let CLIENT_ID: string = APP_CONFIG.USER_POOL_WEB_CLIENT_ID || '';
let OAUTH_DOMAIN: string = APP_CONFIG.OAUTH_DOMAIN || '';

let cognitoClient: CognitoIdentityProviderClient;

// 从 User Pool ID 和 Region 构建 OAuth 域名
function buildOAuthDomain(userPoolId: string, region: string): string {
  // User Pool ID 格式: {region}_{pool-name}
  // 例如: ap-northeast-1_uPKbpC0UY
  // OAuth 域名格式: {pool-name}.auth.{region}.amazoncognito.com
  // 例如: uPKbpC0UY.auth.ap-northeast-1.amazoncognito.com
  const poolName = userPoolId.split('_')[1];
  if (!poolName) {
    throw new Error(`无法从 User Pool ID ${userPoolId} 提取 pool name`);
  }
  return `${poolName}.auth.${region}.amazoncognito.com`;
}

// 清理 OAuth 域名，移除协议前缀和尾部斜杠
function cleanOAuthDomain(domain: string): string {
  // 移除 http:// 或 https:// 前缀
  let cleaned = domain.replace(/^https?:\/\//, '');
  // 移除尾部斜杠
  cleaned = cleaned.replace(/\/$/, '');
  return cleaned;
}

// 获取 OAuth 域名
// 优先级：1. 从 Infisical 加载的 OAUTH_DOMAIN 配置
//        2. 如果配置为空，则从 User Pool ID 和 Region 自动构建
function getOAuthDomain(): string {
  // 优先使用从 Infisical 加载的 OAUTH_DOMAIN 配置
  if (OAUTH_DOMAIN) {
    // 清理域名，移除可能存在的协议前缀
    return cleanOAuthDomain(OAUTH_DOMAIN);
  }
  // 如果未配置，则从 User Pool ID 和 Region 自动构建
  if (USER_POOL_ID && REGION) {
    const autoDomain = buildOAuthDomain(USER_POOL_ID, REGION);
    logger.warn('OAUTH_DOMAIN 未配置，使用自动构建的域名:', autoDomain);
    return autoDomain;
  }
  throw new Error('OAuth 域名未配置，且无法从 User Pool ID 构建');
}

// 初始化AWS SDK配置
export const initializeAwsSdk = async () => {
  try {
    // 从 generated.ts 读取配置（已在构建时注入）
    REGION = (APP_CONFIG.AWS_REGION || REGION) as string;
    USER_POOL_ID = (APP_CONFIG.USER_POOL_ID || USER_POOL_ID) as string;
    CLIENT_ID = (APP_CONFIG.USER_POOL_WEB_CLIENT_ID || CLIENT_ID) as string;
    OAUTH_DOMAIN = (APP_CONFIG.OAUTH_DOMAIN || OAUTH_DOMAIN) as string;
    
    cognitoClient = new CognitoIdentityProviderClient({ region: REGION });
    
    // 尝试获取 OAuth 域名（用于日志）
    let oauthDomainForLog: string = OAUTH_DOMAIN;
    if (!oauthDomainForLog && USER_POOL_ID && REGION) {
      try {
        oauthDomainForLog = buildOAuthDomain(USER_POOL_ID, REGION);
      } catch (e) {
        oauthDomainForLog = '未配置';
      }
    }
    
    logger.log('AWS SDK配置初始化完成:', {
      region: REGION,
      hasUserPoolId: !!USER_POOL_ID,
      hasClientId: !!CLIENT_ID,
      oauthDomain: oauthDomainForLog
    });
  } catch (error) {
    logger.error('AWS SDK配置初始化失败:', error);
    // 使用默认配置
    cognitoClient = new CognitoIdentityProviderClient({ region: REGION });
  }
};

// 用户注册
export const signUpUserWithSDK = async (email: string, password: string) => {
  try {
    logger.log('=== 使用AWS SDK注册用户 ===');
    logger.log('邮箱:', email);
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const command = new SignUpCommand({
      ClientId: CLIENT_ID,
      Username: email,
      Password: password,
      UserAttributes: [
        {
          Name: 'email',
          Value: email
        }
        // 注意：不在注册时设置自定义属性（custom:daily_ai_count 和 custom:last_reset_date）
        // 这些属性应该在用户确认注册后，通过 UpdateUserAttributes 来设置
        // 这样可以避免 Cognito User Pool 中属性未定义或类型不匹配的错误
      ]
    });

    logger.log('发送注册请求...');
    const result = await cognitoClient.send(command);
    logger.log('=== 注册成功 ===');
    logger.log('注册结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('=== 注册失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || '注册失败' };
  }
};

// 用户登录
export const signInUserWithSDK = async (email: string, password: string) => {
  try {
    logger.log('=== 使用AWS SDK登录用户 ===');
    logger.log('邮箱:', email);
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    // 创建SRP会话（传入原始密码，isHashed=false）
    const srpSession = await createSrpSession(email, password, USER_POOL_ID, false);
    
    // 第一步：发起认证请求
    const command = new InitiateAuthCommand({
      ClientId: CLIENT_ID,
      AuthFlow: 'USER_SRP_AUTH',
      AuthParameters: {
        USERNAME: email,
        SRP_A: srpSession.largeA
      }
    });

    logger.log('发送初始认证请求...');
    const result = await cognitoClient.send(command);
    logger.log('初始认证响应:', result);
    
    // 检查是否需要响应挑战
    if (result.ChallengeName === 'PASSWORD_VERIFIER') {
      logger.log('需要响应PASSWORD_VERIFIER挑战');
      
      const challengeParameters = result.ChallengeParameters;
      if (!challengeParameters) {
        throw new Error('挑战参数缺失');
      }
      
      logger.log('需要响应PASSWORD_VERIFIER挑战');
      
      // 使用SRP会话签名挑战响应
      const signedSession = await signSrpSession(
        srpSession,
        { ChallengeParameters: challengeParameters }
      );
      
      // 构建挑战响应
      const challengeResponses = {
        USERNAME: challengeParameters.USERNAME,
        PASSWORD_CLAIM_SECRET_BLOCK: signedSession.secret,
        PASSWORD_CLAIM_SIGNATURE: signedSession.passwordSignature,
        TIMESTAMP: signedSession.timestamp
      };
      
      // 响应挑战
      const respondCommand = new RespondToAuthChallengeCommand({
        ClientId: CLIENT_ID,
        ChallengeName: 'PASSWORD_VERIFIER',
        ChallengeResponses: challengeResponses
      });
      
      logger.log('发送挑战响应...');
      const finalResult = await cognitoClient.send(respondCommand);
      logger.log('=== 登录成功 ===');
      logger.log('最终登录结果:', finalResult);
      
      // 存储token到localStorage
      if (finalResult.AuthenticationResult) {
        const { AccessToken, IdToken, RefreshToken } = finalResult.AuthenticationResult;
        if (AccessToken) {
          localStorage.setItem('cognito_access_token', AccessToken);
        }
        if (IdToken) {
          localStorage.setItem('cognito_id_token', IdToken);
        }
        if (RefreshToken) {
          localStorage.setItem('cognito_refresh_token', RefreshToken);
        }
        logger.log('认证成功，Token已存储');
      }
      
      return { success: true, result: finalResult };
    } else {
      // 如果没有挑战，直接返回结果
      logger.log('=== 登录成功 ===');
      logger.log('登录结果:', result);
      return { success: true, result };
    }
  } catch (error: any) {
    logger.error('=== 登录失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    
    // 识别密码错误
    let errorMessage = error.message || '登录失败';
    
    // 检查是否是密码错误（多种可能的错误情况）
    const errorName = error.name || '';
    const errorCode = error.code || '';
    const errorMsg = (error.message || '').toLowerCase();
    
    const isPasswordError = 
      errorName === 'NotAuthorizedException' || 
      errorCode === 'NotAuthorizedException' ||
      errorName === 'INCORRECT_PASSWORD' ||
      error.message === 'INCORRECT_PASSWORD' ||
      errorMsg.includes('incorrect username or password') ||
      errorMsg.includes('password attempts exceeded') ||
      errorMsg.includes('invalid password') ||
      errorMsg.includes('not authorized') ||
      errorMsg.includes('authentication failed') ||
      errorMsg.includes('incorrect password') ||
      errorMsg.includes('password does not match') ||
      errorMsg.includes('wrong password');
    
    if (isPasswordError) {
      logger.log('识别为密码错误，返回特殊标识');
      errorMessage = 'INCORRECT_PASSWORD'; // 使用特殊标识，让前端显示中英文提示
    } else {
      logger.log('非密码错误，返回原始错误信息');
    }
    
    return { success: false, error: errorMessage };
  }
};

// 确认注册
export const confirmSignUpWithSDK = async (email: string, code: string) => {
  try {
    logger.log('=== 使用AWS SDK确认注册 ===');
    logger.log('邮箱:', email);
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const command = new ConfirmSignUpCommand({
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code
    });

    logger.log('发送确认注册请求...');
    const result = await cognitoClient.send(command);
    logger.log('=== 确认注册成功 ===');
    logger.log('确认结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('=== 确认注册失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || '确认注册失败' };
  }
};

// 重新发送验证码
export const resendSignUpWithSDK = async (email: string) => {
  try {
    logger.log('=== 使用AWS SDK重新发送验证码 ===');
    logger.log('邮箱:', email);
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const command = new ResendConfirmationCodeCommand({
      ClientId: CLIENT_ID,
      Username: email
    });

    logger.log('发送重新发送验证码请求...');
    const result = await cognitoClient.send(command);
    logger.log('=== 重新发送成功 ===');
    logger.log('结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('=== 重新发送失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || '重新发送失败' };
  }
};

// 忘记密码 - 发送验证码
export const forgotPasswordWithSDK = async (email: string) => {
  try {
    logger.log('=== 使用AWS SDK忘记密码 ===');
    logger.log('邮箱:', email);
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const command = new ForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: email
    });

    logger.log('发送忘记密码请求...');
    const result = await cognitoClient.send(command);
    logger.log('=== 忘记密码验证码已发送 ===');
    logger.log('结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('=== 忘记密码失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || '发送验证码失败' };
  }
};

// 确认忘记密码 - 重置密码
export const confirmForgotPasswordWithSDK = async (email: string, code: string, newPassword: string) => {
  try {
    logger.log('=== 使用AWS SDK确认忘记密码 ===');
    logger.log('邮箱:', email);
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const command = new ConfirmForgotPasswordCommand({
      ClientId: CLIENT_ID,
      Username: email,
      ConfirmationCode: code,
      Password: newPassword
    });

    logger.log('发送确认忘记密码请求...');
    const result = await cognitoClient.send(command);
    logger.log('=== 密码重置成功 ===');
    logger.log('结果:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('=== 确认忘记密码失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || '密码重置失败' };
  }
};

// Google OAuth登录 - 使用AWS SDK
export const signInWithGoogleSDK = async () => {
  try {
    logger.log('=== 使用AWS SDK进行Google OAuth登录 ===');
    
    // 检查必要的配置
    if (!CLIENT_ID) {
      throw new Error('客户端ID未配置');
    }
    
    // 获取 OAuth 域名
    const oauthDomain = getOAuthDomain();
    logger.log('OAuth域名:', oauthDomain);
    
    // 构建Google OAuth URL
    const redirectUri = encodeURIComponent(window.location.origin + '/');
    const state = Math.random().toString(36).substring(2, 15);
    
    // 存储state用于验证
    sessionStorage.setItem('oauth_state', state);
    
    const oauthUrl = `https://${oauthDomain}/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=openid&` +
      `identity_provider=Google&` +
      `state=${state}`;
    
    logger.log('重定向到Google OAuth:', oauthUrl);
    
    // 重定向到Google OAuth
    window.location.href = oauthUrl;
    
    return { success: true };
  } catch (error: any) {
    logger.error('=== Google OAuth登录失败 ===');
    logger.error('错误详情:', error.message);
    return { success: false, error: error.message || 'Google OAuth登录失败' };
  }
};


// 备用方案：使用不同的Google身份提供商参数格式
export const signInWithGoogleBasic = async () => {
  try {
    logger.log('=== 使用基本OAuth进行Google登录 ===');
    
    // 检查必要的配置
    if (!CLIENT_ID) {
      throw new Error('客户端ID未配置');
    }
    
    // 获取 OAuth 域名
    const oauthDomain = getOAuthDomain();
    logger.log('OAuth域名:', oauthDomain);
    
    // 构建Google OAuth URL - 尝试不同的Google身份提供商参数格式
    const redirectUri = encodeURIComponent(window.location.origin + '/');
    const state = Math.random().toString(36).substring(2, 15);
    
    // 存储state用于验证
    sessionStorage.setItem('oauth_state', state);
    
    // 尝试使用provider参数而不是identity_provider
    const oauthUrl = `https://${oauthDomain}/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=openid&` +
      `provider=Google&` +
      `state=${state}`;
    
    logger.log('重定向到OAuth:', oauthUrl);
    
    // 重定向到OAuth
    window.location.href = oauthUrl;
    
    return { success: true };
  } catch (error: any) {
    logger.error('=== 基本OAuth登录失败 ===');
    logger.error('错误详情:', error.message);
    return { success: false, error: error.message || '基本OAuth登录失败' };
  }
};

// 备用方案：使用idp参数
export const signInWithGoogleIdp = async () => {
  try {
    logger.log('=== 使用idp参数进行Google登录 ===');
    
    // 检查必要的配置
    if (!CLIENT_ID) {
      throw new Error('客户端ID未配置');
    }
    
    // 获取 OAuth 域名
    const oauthDomain = getOAuthDomain();
    logger.log('OAuth域名:', oauthDomain);
    
    // 构建Google OAuth URL - 使用idp参数
    const redirectUri = encodeURIComponent(window.location.origin + '/');
    const state = Math.random().toString(36).substring(2, 15);
    
    // 存储state用于验证
    sessionStorage.setItem('oauth_state', state);
    
    // 尝试使用idp参数
    const oauthUrl = `https://${oauthDomain}/oauth2/authorize?` +
      `response_type=code&` +
      `client_id=${CLIENT_ID}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=openid&` +
      `idp=Google&` +
      `state=${state}`;
    
    logger.log('重定向到OAuth:', oauthUrl);
    
    // 重定向到OAuth
    window.location.href = oauthUrl;
    
    return { success: true };
  } catch (error: any) {
    logger.error('=== idp参数OAuth登录失败 ===');
    logger.error('错误详情:', error.message);
    return { success: false, error: error.message || 'idp参数OAuth登录失败' };
  }
};

// 备用方案：直接使用Amplify的Google登录
export const signInWithGoogleAmplify = async () => {
  try {
    logger.log('=== 使用AWS Amplify进行Google OAuth登录 ===');
    
    // 动态导入Amplify以避免循环依赖
    const { signInWithRedirect } = await import('aws-amplify/auth');
    
    await signInWithRedirect({ provider: 'Google' });
    logger.log('Google登录重定向已启动');
    return { success: true };
  } catch (error: any) {
    logger.error('=== Amplify Google OAuth登录失败 ===');
    logger.error('错误详情:', {
      name: error.name,
      message: error.message,
      code: error.code,
      stack: error.stack
    });
    return { success: false, error: error.message || 'Amplify Google OAuth登录失败' };
  }
};

// 处理Google OAuth回调
export const handleGoogleOAuthCallback = async (code: string, state: string) => {
  try {
    logger.log('=== 处理Google OAuth回调 ===');
    
    // 验证state
    const storedState = sessionStorage.getItem('oauth_state');
    if (state !== storedState) {
      logger.warn('State验证失败，但继续处理');
    }
    
    // 暂时不清除state，等token交换成功后再清除
    // sessionStorage.removeItem('oauth_state');
    
    // 检查必要的配置
    if (!CLIENT_ID) {
      throw new Error('客户端ID未配置');
    }
    
    // 获取 OAuth 域名
    const oauthDomain = getOAuthDomain();
    logger.log('OAuth域名:', oauthDomain);
    
    // 使用授权码获取token
    const tokenUrl = `https://${oauthDomain}/oauth2/token`;
    const redirectUri = window.location.origin + '/';
    
    const tokenParams = new URLSearchParams();
    tokenParams.append('grant_type', 'authorization_code');
    tokenParams.append('client_id', CLIENT_ID);
    tokenParams.append('code', code);
    tokenParams.append('redirect_uri', redirectUri);
    
    logger.log('请求token URL:', tokenUrl);
    
    const tokenResponse = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: tokenParams.toString()
    });
    
    if (!tokenResponse.ok) {
      const errorText = await tokenResponse.text();
      logger.error('Token请求失败:', errorText);
      
      // 尝试解析错误响应
      let errorDetails = errorText;
      try {
        const errorJson = JSON.parse(errorText);
        errorDetails = JSON.stringify(errorJson, null, 2);
        logger.error('错误详情:', errorJson);
        
        // 如果是invalid_grant错误，提供更详细的说明
        if (errorJson.error === 'invalid_grant') {
          logger.error('invalid_grant错误可能的原因:');
          logger.error('1. 授权码已过期（通常10分钟有效期）');
          logger.error('2. 授权码已被使用过');
          logger.error('3. redirect_uri不匹配');
          logger.error('4. 客户端密钥错误');
          logger.error('5. 授权码格式错误');
        }
        
        // 如果是invalid_request错误，提供更详细的说明
        if (errorJson.error === 'invalid_request') {
          logger.error('invalid_request错误可能的原因:');
          logger.error('1. 请求参数格式错误');
          logger.error('2. 缺少必需的参数');
          logger.error('3. 参数值无效');
          logger.error('4. 客户端配置错误');
          logger.error('5. 重定向URI不匹配');
        }
      } catch (e) {
        logger.error('无法解析错误响应:', e);
      }
      
      throw new Error(`Token请求失败: ${tokenResponse.status} ${errorDetails}`);
    }
    
    const tokenData = await tokenResponse.json();
    
    // 存储token到localStorage
    if (tokenData.access_token) {
      localStorage.setItem('cognito_access_token', tokenData.access_token);
    }
    if (tokenData.id_token) {
      localStorage.setItem('cognito_id_token', tokenData.id_token);
    }
    if (tokenData.refresh_token) {
      localStorage.setItem('cognito_refresh_token', tokenData.refresh_token);
    }
    
    logger.log('OAuth认证成功，Token已存储');
    
    // 清除OAuth state和处理标记
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_processing');
    
    return { success: true, tokens: tokenData };
  } catch (error: any) {
    logger.error('=== Google OAuth回调处理失败 ===');
    logger.error('错误详情:', error.message);
    
    // 清除OAuth state和处理标记，即使处理失败
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_processing');
    
    return { success: false, error: error.message || 'OAuth回调处理失败' };
  }
};

// 检查token是否过期
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload = JSON.parse(jsonPayload);
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch (error) {
    logger.error('解析token失败:', error);
    return true; // 如果解析失败，认为已过期
  }
};

// 检查是否已通过AWS SDK登录（同时检查token是否过期）
export const isAuthenticatedWithSDK = (): boolean => {
  const accessToken = localStorage.getItem('cognito_access_token');
  const idToken = localStorage.getItem('cognito_id_token');
  
  if (!accessToken || !idToken) {
    return false;
  }
  
  // 检查token是否过期
  if (isTokenExpired(accessToken) || isTokenExpired(idToken)) {
    logger.warn('Token已过期');
    return false;
  }
  
  return true;
};

// 获取当前用户信息 - AWS SDK版本
export const getCurrentUserWithSDK = async () => {
  try {
    const accessToken = localStorage.getItem('cognito_access_token');
    if (!accessToken) {
      throw new Error('没有访问令牌');
    }
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const command = new GetUserCommand({
      AccessToken: accessToken
    });
    
    const result = await cognitoClient.send(command);
    logger.log('用户信息获取成功:', result);
    
    return {
      username: result.Username,
      attributes: result.UserAttributes?.reduce((acc, attr) => {
        acc[attr.Name!] = attr.Value!;
        return acc;
      }, {} as Record<string, string>)
    };
  } catch (error: any) {
    logger.error('获取用户信息失败:', error);
    return null;
  }
};

// 更新当前用户信息 - AWS SDK版本
export const updateUserAttributesWithSDK = async (attributes: { [key: string]: string }) => {
  try {
    logger.log('=== 使用AWS SDK更新用户信息 ===', attributes);
    const accessToken = localStorage.getItem('cognito_access_token');
    if (!accessToken) {
      throw new Error('没有访问令牌');
    }
    
    // 检查cognitoClient是否已初始化
    if (!cognitoClient) {
      logger.error('cognitoClient未初始化，尝试重新初始化...');
      await initializeAwsSdk();
      if (!cognitoClient) {
        throw new Error('cognitoClient初始化失败');
      }
    }
    
    const userAttributes = Object.entries(attributes).map(([key, value]) => ({
      Name: key,
      Value: value
    }));
    
    const command = new UpdateUserAttributesCommand({
      AccessToken: accessToken,
      UserAttributes: userAttributes
    });
    
    const result = await cognitoClient.send(command);
    logger.log('用户信息更新成功:', result);
    return { success: true, result };
  } catch (error: any) {
    logger.error('更新用户信息失败:', error);
    return { success: false, error: error.message || '更新失败' };
  }
};

// 从本地ID Token解析用户信息，用于快速显示
export const getUserInfoFromLocalToken = () => {
  try {
    const idToken = localStorage.getItem('cognito_id_token');
    if (!idToken) return null;
    
    const base64Url = idToken.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload) as any;
    logger.log('解析ID Token Payload:', payload);
    
    // 构造与getCurrentUserWithSDK兼容的返回格式
    return {
      username: payload['cognito:username'] || payload.sub,
      attributes: payload
    };
  } catch (e) {
    logger.error('解析ID Token失败:', e);
    return null;
  }
};

// 解析本地ID Token，提取用于调用后端的身份信息
export const getUserIdentityForApi = () => {
  try {
    const idToken = localStorage.getItem('cognito_id_token');
    if (!idToken) return null;
    const base64Url = idToken.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const payload = JSON.parse(jsonPayload) as any;
    const email: string = (payload.email || '').toLowerCase();
    const sub: string = payload.sub;
    // 为了后端一致性，userId 明确等于 Cognito sub。
    const userId = sub;
    return { userId, email, sub, payload };
  } catch (e) {
    logger.error('解析ID Token失败，无法获取用户身份:', e);
    return null;
  }
};

// 登出 - AWS SDK版本
export const signOutWithSDK = async () => {
  try {
    // 清除本地存储的token
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_refresh_token');
    
    // 清除sessionStorage中的OAuth state
    sessionStorage.removeItem('oauth_state');
    
    logger.log('退出登录成功');
    
    // 不重定向到Cognito登出URL，直接返回成功
    // 让应用自己处理退出登录后的状态
    return { success: true };
  } catch (error: any) {
    logger.error('登出失败:', error);
    return { success: false, error: error.message };
  }
};
